import React from 'react';
import Card from '../../../Presentation/components/ui/Card';
import {AppText, AppSmallText, AppTitle, AppSectionTitle, AppBodyText, AppCardTitle, AppLabelText} from '../../../Presentation/components/ui/Typography';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';
import {PaymentStatistics as PaymentStatsModel} from '../models/Payment';
import {FileText, DollarSign, TrendingUp, CheckCircle, AlertCircle, Clock} from 'lucide-react';
import {CardLoadingState} from '../../../Presentation/components/loading/CardLoadingState';

interface PaymentStatisticsProps {
  statistics: PaymentStatsModel | null;
  loading?: boolean;
}

const PaymentStatistics: React.FC<PaymentStatisticsProps> = ({
  statistics,
  loading = false
}) => {
  if (loading) {
    return <CardLoadingState count={6} columns={4} />;
  }

  if (!statistics) {
    return (
      <Card className="p-6">
        <AppText className="text-gray-500 text-center">لا توجد إحصائيات متاحة</AppText>
      </Card>
    );
  }

  // Use the actual API response structure
  const totalInvoices = statistics.total_invoices ?? 0;
  const paidInvoices = statistics.paid_invoices ?? 0;
  const partiallyPaidInvoices = statistics.partially_paid_invoices ?? 0;
  const unpaidInvoices = statistics.unpaid_invoices ?? 0;
  const totalAmount = statistics.total_amount ?? 0;
  const totalPaid = statistics.total_paid ?? 0;
  const totalRemaining = statistics.total_remaining ?? 0;

  const statsCards = [
    {
      title: 'إجمالي الفواتير',
      value: totalInvoices.toLocaleString(),
      icon: <FileText className="w-6 h-6" />,
      color: 'text-brand-600',
      bgColor: 'bg-brand-50'
    },
    {
      title: 'الفواتير المدفوعة',
      value: paidInvoices.toLocaleString(),
      icon: <CheckCircle className="w-6 h-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'الفواتير المدفوعة جزئياً',
      value: partiallyPaidInvoices.toLocaleString(),
      icon: <Clock className="w-6 h-6" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'الفواتير غير المدفوعة',
      value: unpaidInvoices.toLocaleString(),
      icon: <AlertCircle className="w-6 h-6" />,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      title: 'إجمالي المبالغ',
      value: formatCurrency(totalAmount),
      icon: <DollarSign className="w-6 h-6" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'المبلغ المدفوع',
      value: formatCurrency(totalPaid),
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'المبلغ المتبقي',
      value: formatCurrency(totalRemaining),
      icon: <AlertCircle className="w-6 h-6" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <AppSmallText className="text-gray-600 mb-1">{stat.title}</AppSmallText>
                <AppTitle className="text-gray-900">{stat.value}</AppTitle>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <div className={stat.color}>
                  {stat.icon}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Customers */}
        {statistics.top_customers && statistics.top_customers.length > 0 && (
          <Card className="p-6">
            <AppSectionTitle className="font-semibold text-gray-900 mb-4">أفضل العملاء</AppSectionTitle>
            <div className="space-y-3">
              {statistics.top_customers.slice(0, 5).map((customer, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <AppBodyText className="font-medium text-gray-900">{customer.customer_name}</AppBodyText>
                    <AppSmallText className="text-gray-500">
                      مدفوع: {formatCurrency(customer.paid_amount)}
                    </AppSmallText>
                  </div>
                  <div className="text-right">
                    <AppText className="font-semibold text-brand-600">
                      {formatCurrency(customer.total_amount)}
                    </AppText>
                    <AppSmallText className="text-gray-500">إجمالي</AppSmallText>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Aging Report */}
        {statistics.aging_report && (
          <Card className="p-6">
            <AppSectionTitle className="font-semibold text-gray-900 mb-4">تقرير أعمار الديون</AppSectionTitle>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <AppBodyText className="text-gray-600">الحالية</AppBodyText>
                <AppText className="font-semibold text-green-600">
                  {formatCurrency(statistics.aging_report.current)}
                </AppText>
              </div>
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                <AppBodyText className="text-gray-600">1-30 يوم</AppBodyText>
                <AppText className="font-semibold text-yellow-600">
                  {formatCurrency(statistics.aging_report.days_1_30)}
                </AppText>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <AppBodyText className="text-gray-600">31-60 يوم</AppBodyText>
                <AppText className="font-semibold text-orange-600">
                  {formatCurrency(statistics.aging_report.days_31_60)}
                </AppText>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <AppBodyText className="text-gray-600">61-90 يوم</AppBodyText>
                <AppText className="font-semibold text-red-600">
                  {formatCurrency(statistics.aging_report.days_61_90)}
                </AppText>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-100 rounded-lg">
                <AppBodyText className="text-gray-600">أكثر من 90 يوم</AppBodyText>
                <AppText className="font-semibold text-red-700">
                  {formatCurrency(statistics.aging_report.over_90)}
                </AppText>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Payment Progress Bar */}
      <Card className="p-6">
        <AppSectionTitle className="font-semibold text-gray-900 mb-4">نسبة التحصيل</AppSectionTitle>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <AppBodyText className="text-gray-600">المدفوع</AppBodyText>
              <AppBodyText className="font-medium text-green-600">
                {totalAmount > 0 ? ((totalPaid / totalAmount) * 100).toFixed(1) : 0}%
              </AppBodyText>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-600 h-3 rounded-full transition-all duration-300"
                style={{width: `${totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0}%`}}
              ></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <AppLabelText className="text-gray-600 mb-1">المبلغ المدفوع</AppLabelText>
              <AppCardTitle className="font-bold text-green-600">
                {formatCurrency(totalPaid)}
              </AppCardTitle>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <AppLabelText className="text-gray-600 mb-1">المبلغ المتبقي</AppLabelText>
              <AppCardTitle className="font-bold text-orange-600">
                {formatCurrency(totalRemaining)}
              </AppCardTitle>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PaymentStatistics;
